/**
 * The one list of "how to connect your AI client" instructions.
 *
 * Rendered by the public docs page (`pages/DocsPage.tsx`, www.conducktor.com/docs) and by
 * the Account page's AI Clients tab. Two hand-written copies drifted apart before this
 * existed, and one of them told people to put a remote server in
 * `claude_desktop_config.json`, which Anthropic documents as local-servers-only.
 *
 * Every entry is written against the vendor's own documentation, re-read 2026-10-05:
 * support.claude.com (custom connectors), code.claude.com/docs/en/mcp (Claude Code),
 * developers.openai.com/api/docs/mcp (ChatGPT), cursor.com/docs/context/mcp (Cursor),
 * code.visualstudio.com/docs/copilot/chat/mcp-servers (VS Code).
 *
 * `{url}` in a step stands for the connect URL (`MCP_CONNECT_URL`); renderers replace it,
 * and `withUrl` does the same for plain text.
 */

export type McpClientSetup =
  | { kind: 'steps'; steps: readonly string[] }
  | { kind: 'command'; command: (url: string) => string; after?: string }
  | { kind: 'config'; file: string; config: (url: string) => string };

export interface McpClient {
  id: 'claude' | 'claude-code' | 'chatgpt' | 'cursor' | 'vscode' | 'other';
  name: string;
  emoji: string;
  intro: string;
  setup: McpClientSetup;
  note?: string;
}

export const URL_PLACEHOLDER = '{url}';

/** Replace every `{url}` in plain text. Renderers that want inline code split on it instead. */
export function withUrl(text: string, url: string): string {
  return text.split(URL_PLACEHOLDER).join(url);
}

const jsonConfig = (value: unknown) => JSON.stringify(value, null, 2);

export const MCP_CLIENTS: readonly McpClient[] = [
  {
    id: 'claude',
    name: 'Claude',
    emoji: '🧠',
    intro: 'Claude on the web, Claude Desktop and Cowork share one list of connectors.',
    setup: {
      kind: 'steps',
      steps: [
        'Open Customize → Connectors.',
        'Click + Add, then Add custom connector.',
        'Name it Conducktor and paste {url}. Click Continue.',
        'Under OAuth client choose Register automatically. Under Authentication choose Sign in now. Click Add.',
        'Sign in with your Conducktor account and approve the connection.',
      ],
    },
    note:
      'On a Team plan only an Owner can add a connector, under Organization settings → Connectors; ' +
      'on Enterprise, an Owner or a role with Manage access to Libraries. Free accounts get one custom connector. ' +
      'The claude_desktop_config.json file is for local servers only; a remote server is added here, not there.',
  },
  {
    id: 'claude-code',
    name: 'Claude Code',
    emoji: '⌨️',
    intro: 'Add the server from the terminal, then sign in.',
    setup: {
      kind: 'command',
      command: (url) => `claude mcp add --transport http conducktor ${url}`,
      after: 'Then run /mcp inside Claude Code and follow the sign-in link.',
    },
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    emoji: '💬',
    intro: 'ChatGPT adds remote MCP servers in Developer mode.',
    setup: {
      kind: 'steps',
      steps: [
        'Open Settings → Security and login and turn on Developer mode (in a workspace: Settings → Apps → Advanced settings).',
        'Open the Plugins page (Apps & Connectors in a workspace) and click +.',
        'Name it Conducktor, paste {url}, choose OAuth, and save.',
        'Sign in with your Conducktor account when ChatGPT asks.',
      ],
    },
  },
  {
    id: 'cursor',
    name: 'Cursor',
    emoji: '🖱️',
    intro: 'Cursor reads MCP servers from a JSON file.',
    setup: {
      kind: 'config',
      file: '.cursor/mcp.json (this project) or ~/.cursor/mcp.json (everywhere)',
      config: (url) => jsonConfig({ mcpServers: { conducktor: { url } } }),
    },
    note: 'Cursor opens the sign-in page the first time it connects.',
  },
  {
    id: 'vscode',
    name: 'VS Code / GitHub Copilot',
    emoji: '💻',
    intro: 'VS Code reads MCP servers from mcp.json.',
    setup: {
      kind: 'config',
      file: '.vscode/mcp.json',
      config: (url) => jsonConfig({ servers: { conducktor: { type: 'http', url } } }),
    },
    note: 'Or run MCP: Add Server from the Command Palette and paste the URL.',
  },
  {
    id: 'other',
    name: 'Any other MCP client',
    emoji: '🔌',
    intro: 'Any client that supports remote MCP servers over HTTPS with OAuth works the same way.',
    setup: {
      kind: 'steps',
      steps: [
        'Paste {url} where the client asks for an MCP server URL.',
        "Choose OAuth (or 'sign in') when asked how to authenticate; the client registers itself with Conducktor.",
        'Sign in with your Conducktor account and approve the connection.',
      ],
    },
  },
];

/** Testing aid shown after the client list on both pages. */
export const MCP_INSPECTOR_COMMAND = 'npx @modelcontextprotocol/inspector';
